import { activityTypeForFeatureKind, type ActivityTypeName } from "@monorepo/shared/activityTypes";

import {
  MetaTagKind,
  PatientActivitiesQueryQuery,
  PractitionerActivitiesQueryQuery,
  PractitionerNotesQueryQuery,
  SessionKind,
} from "../generated/graphql";
import { NoteType } from "../types/note";
import { formatName } from "./formatName";

export type PractitionerActivitySession = NonNullable<
  NonNullable<PractitionerActivitiesQueryQuery["user"]>["activitySessionsByCaregiverId"]["nodes"][0]
>;

export type PatientActivitySession = NonNullable<
  NonNullable<
    PatientActivitiesQueryQuery["user"]
  >["activitySessionsByActivityParticipantSeniorIdAndActivitySessionId"]["nodes"][0]
>;

export type ActivitySession = PractitionerActivitySession | PatientActivitySession;

export type NoteActivitySession = NonNullable<
  NonNullable<NonNullable<NonNullable<PractitionerNotesQueryQuery["notes"]>["nodes"][0]>["activitySessionList"]>[0]
>;

export type Participant = NonNullable<
  NonNullable<PractitionerActivitySession["activityParticipants"]["nodes"][0]["senior"]>
>;

type ContentTitleResult = {
  contentTitle: string;
  contentTitleKey: string;
};

// Minimal structural shape the content-derivation helpers below read from an
// activity. Both the generated session types (patient/practitioner) and the
// procedure session type satisfy it, so the helpers can be shared across pages.
export type ActivityContentSource = {
  activity?: {
    contentActivity?: {
      content?: {
        title?: string | null;
        cmsContentKind?: string | null;
        cmsNextContentKind?: string | null;
        metaTags?: {
          nodes: ReadonlyArray<{ metaTag?: { kind?: string | null; tag?: string | null } | null } | null>;
        } | null;
      } | null;
    } | null;
    customActivity?: { name?: string | null } | null;
    featureActivity?: { kind?: string | null } | null;
  } | null;
};

export const getActivityContentTitle = (activitySession: ActivityContentSource): ContentTitleResult => {
  const contentActivity = activitySession.activity?.contentActivity;
  const content = contentActivity?.content;
  const customActivity = activitySession.activity?.customActivity;
  const featureActivity = activitySession.activity?.featureActivity;

  let contentTitle = "";
  let contentTitleKey = "";

  if (content?.title) {
    contentTitle = content.title;
  } else if (customActivity?.name) {
    contentTitle = customActivity.name;
    contentTitleKey = `subActivities.${customActivity.name}`;
  } else if (featureActivity?.kind) {
    contentTitleKey = `featureActivityKind.${featureActivity.kind}`;
  }

  return {
    contentTitle,
    contentTitleKey,
  };
};

const activityTypeSlugs: Record<ActivityTypeName, string> = {
  COGNITIVE: "cognitive",
  PHYSICAL: "physical",
  SOCIAL_AND_COMMUNICATIVE: "social-and-communicative",
  MENTAL_WELLBEING: "mental-wellbeing",
};

export const getActivityType = (activitySession: ActivityContentSource | NoteActivitySession | null): string | null => {
  if (!activitySession) return null;

  const content = activitySession.activity?.contentActivity?.content;
  const customActivity = activitySession.activity?.customActivity;
  const featureActivity = activitySession.activity?.featureActivity;

  const activityTypeMetaTag = content?.metaTags?.nodes?.find(
    (metaTag): metaTag is NonNullable<typeof metaTag> => metaTag?.metaTag?.kind === MetaTagKind.ActivityType
  );

  if (activityTypeMetaTag?.metaTag?.tag) {
    return activityTypeMetaTag.metaTag.tag;
  }

  if (customActivity?.name) {
    const customActivityMap: Record<string, string> = {
      // COGNITIVE
      quiz: "cognitive",
      language: "cognitive",
      "numbers-and-counting": "cognitive",
      "music-and-sounds": "cognitive",
      "memory-games": "cognitive",
      "handicraft-and-creativity": "cognitive",
      // PHYSICAL
      walk: "physical",
      "staircase-training": "physical",
      "seated-gymnastics": "physical",
      "hand-gymnastics": "physical",
      "fall-prevention": "physical",
      "movement-games": "physical",
      // SOCIAL_AND_COMMUNICATIVE
      "telephone-call": "social-and-communicative",
      visit: "social-and-communicative",
      "group-participation": "social-and-communicative",
      "event-visit": "social-and-communicative",
      "roommate-meeting": "social-and-communicative",
      // MENTAL_WELLBEING
      "relaxation-exercise": "mental-wellbeing",
      snoezelen: "mental-wellbeing",
      "relieving-talk": "mental-wellbeing",
      hobby: "mental-wellbeing",
      "basal-stimulation": "mental-wellbeing",
      wellness: "mental-wellbeing",
    };
    return customActivityMap[customActivity.name] || null;
  }

  const featureActivityType = activityTypeForFeatureKind(featureActivity?.kind);
  if (featureActivityType) return activityTypeSlugs[featureActivityType];

  return null;
};

export const getCmsContentKind = (activitySession: ActivityContentSource): string | null => {
  const content = activitySession.activity?.contentActivity?.content;
  return content?.cmsNextContentKind || content?.cmsContentKind || null;
};

export const calculateDurationInMinutes = (beganAt: string, endedAt?: string | null): string | null => {
  if (!endedAt) return null;
  const durationInMinutes = (new Date(endedAt).getTime() - new Date(beganAt).getTime()) / 1000 / 60;
  return durationInMinutes < 1 ? "<1" : Math.ceil(durationInMinutes).toString();
};

export type Activity = {
  id: string;
  beganAt: string;
  endedAt: string | null;
  duration: string | null;
  contentTitle: string;
  contentTitleKey: string;
  activityType: string | null;
  cmsContentKind: string | null;
  kind: SessionKind;
  participants?: Participant[];
  notes?: NoteType[];
};

export const mapActivitySession = (activitySession: ActivitySession): Activity => {
  const { contentTitle, contentTitleKey } = getActivityContentTitle(activitySession);
  const activityType = getActivityType(activitySession);
  const cmsContentKind = getCmsContentKind(activitySession);

  const participants =
    "activityParticipants" in activitySession
      ? activitySession.activityParticipants.nodes.filter(p => p.senior != null).map(p => p.senior!)
      : undefined;

  const notes =
    "noteList" in activitySession
      ? activitySession.noteList?.map(note => ({
          id: note.id,
          note: note.note,
          kind: note.kind,
          generationStatus: note.generationStatus,
          publishedAt: note.publishedAt,
          documentationSyncs: note.documentationSyncsList,
          mdDocumentation: note.mdDocumentation,
          createdAt: note.createdAt,
          updatedAt: note.updatedAt,
          authorName: formatName(note.author),
        }))
      : undefined;

  return {
    id: activitySession.id,
    beganAt: activitySession.beganAt,
    endedAt: activitySession.endedAt,
    duration: calculateDurationInMinutes(activitySession.beganAt, activitySession.endedAt),
    contentTitle,
    contentTitleKey,
    activityType,
    cmsContentKind,
    kind: activitySession.kind,
    ...(participants !== undefined && { participants }),
    ...(notes !== undefined && { notes }),
  };
};

export const getParticipantInitials = (participant: Participant): string => {
  if (participant.firstName && participant.lastName) {
    return `${participant.firstName[0]}${participant.lastName[0]}`.toUpperCase();
  }
  if (participant.name) {
    const nameParts = participant.name.split(" ");
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return participant.name[0]?.toUpperCase() || "";
  }
  return "";
};
